import { getCategoryFromItemName } from '@utils/cartUtils';
import { ConfigurePlanDialog } from '@components/ProductOffers/configuration/ConfigurePlanDialog/ConfigurePlanDialog';
import { PlanDetailsModal } from '@components/ProductOffers/modals/PlanDetailsModal';
import { WarningDialog } from '@shared/components/WarningDialog/WarningDialog';
import type { useEditPlan } from '@hooks/useEditPlan/useEditPlan';
import type { CategoryType, Plan } from '@t/productOffering.types';

interface CartDrawerDialogsProps {
  removeAllDialogOpen: boolean;
  setRemoveAllDialogOpen: (open: boolean) => void;
  confirmRemoveAll: () => void;
  deleteInternetDialogOpen: boolean;
  closeDeleteInternetDialog: () => void;
  confirmDeleteInternet: () => void;
  isCartMutating: boolean;
  detailsPlan: Plan | null;
  closeDetails: () => void;
  editPlan: ReturnType<typeof useEditPlan>;
}

export const CartDrawerDialogs = ({
  removeAllDialogOpen,
  setRemoveAllDialogOpen,
  confirmRemoveAll,
  deleteInternetDialogOpen,
  closeDeleteInternetDialog,
  confirmDeleteInternet,
  isCartMutating,
  detailsPlan,
  closeDetails,
  editPlan,
}: CartDrawerDialogsProps) => (
  <>
    {/* Remove All Items confirmation dialog */}
    <WarningDialog
      open={removeAllDialogOpen}
      onClose={() => setRemoveAllDialogOpen(false)}
      title="Empty your cart?"
      description="Are you sure you want to remove all items from your cart? This cannot be undone."
      outlinedButtonText="CANCEL"
      containedButtonText="EMPTY CART"
      loading={isCartMutating}
      onOutlinedClick={() => setRemoveAllDialogOpen(false)}
      onContainedClick={confirmRemoveAll}
    />

    {/* Delete Internet plan warning dialog */}
    <WarningDialog
      open={deleteInternetDialogOpen}
      onClose={closeDeleteInternetDialog}
      title="Internet Connection Required"
      description="To provide TV or Phone services, a Novus internet plan must remain in your cart. Removing internet will also remove all other services from your current order."
      outlinedButtonText="KEEP MY PLAN"
      containedButtonText="EMPTY CART"
      loading={isCartMutating}
      onOutlinedClick={closeDeleteInternetDialog}
      onContainedClick={confirmDeleteInternet}
    />

    {/* Plan Details Modal */}
    <PlanDetailsModal
      open={!!detailsPlan}
      onClose={closeDetails}
      plan={detailsPlan}
      categoryType={(getCategoryFromItemName(detailsPlan?.name) || 'Internet') as CategoryType}
    />

    {/* Edit Plan Configuration Dialog */}
    {editPlan.isEditing && (
      <ConfigurePlanDialog
        editMode={editPlan.editConfig}
        hasUnsavedChanges={editPlan.hasUnsavedChanges}
        editLoading={editPlan.isSaving}
        onSaveComplete={editPlan.handleSaveStep}
        onCancelEdit={editPlan.handleCancelEdit}
      />
    )}
  </>
);
